import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { Button } from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  FileTextOutlined,
  GiftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  NotificationOutlined,
  PictureOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SafetyOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
  TruckOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { myTheme } from "src/styles/GlobalStyle";
import { useAuthStore } from "src/store/Auth/auth.store";
import type { MenuItem } from "src/types/menu/menuTypes";

type SidebarProps = {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
};

const Sidebar = ({ collapsed, setCollapsed }: SidebarProps) => {
  const location = useLocation();
  const menuTree = useAuthStore((s) => s.menuTree);

  // 여러 1단 오픈: index Set
  const [openIndexSet, setOpenIndexSet] = useState<Set<number>>(() => new Set());

  // 여러 2단 오픈: "parentIndex-subIdx" Set
  const [openSubKeySet, setOpenSubKeySet] = useState<Set<string>>(() => new Set());

  // 경로에서 부모 메뉴 인덱스 찾는 함수
  const findParentIndices = useCallback(
    (path: string): { parentIndex: number | null; subKey: string | null } => {
      let foundParentIndex: number | null = null;
      let foundSubKey: string | null = null;

      menuTree.forEach((item, index) => {
        if (item.hasChildren && item.children?.length > 0) {
          item.children.forEach((sub, subIdx) => {
            // 2단계 메뉴가 현재 경로와 일치
            if (sub.menuRouterLink === path) {
              foundParentIndex = index;
            }

            // 3단계 메뉴 확인
            if (sub.hasChildren && sub.children?.length > 0) {
              sub.children.forEach((leaf) => {
                if (leaf.menuRouterLink === path) {
                  foundParentIndex = index;
                  foundSubKey = `${index}-${subIdx}`;
                }
              });
            }
          });
        }
      });

      return { parentIndex: foundParentIndex, subKey: foundSubKey };
    },
    [menuTree]
  );

  // 현재 경로의 부모 메뉴를 자동으로 펼치기 (기존 열린 메뉴는 유지)
  useEffect(() => {
    const currentPath = location.pathname;

    // 대시보드("/")는 처리하지 않음
    if (currentPath === "/") {
      return;
    }

    const { parentIndex, subKey } = findParentIndices(currentPath);

    // 찾은 부모 메뉴를 기존 상태에 추가
    if (parentIndex !== null) {
      setOpenIndexSet((prev) => {
        const next = new Set(prev);
        next.add(parentIndex);
        return next;
      });
    }

    if (subKey !== null) {
      setOpenSubKeySet((prev) => {
        const next = new Set(prev);
        next.add(subKey);
        return next;
      });
    }
  }, [location.pathname, findParentIndices]);

  useEffect(() => {
    const handleResize = () => {
      setCollapsed(window.innerWidth < 1200);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setCollapsed]);

  const isActivePath = (path?: string) => !!path && location.pathname === path;

  const handleToggle = (index: number) => {
    if (collapsed) return;
    setOpenIndexSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

    setOpenSubKeySet((prev) => {
      const next = new Set(prev);
      const willClose = openIndexSet.has(index);

      if (willClose) {
        Array.from(next).forEach((k) => {
          if (k.startsWith(`${index}-`)) next.delete(k);
        });
      }
      return next;
    });
  };

  const handleToggleSub = (parentIndex: number, subIdx: number) => {
    if (collapsed) return;
    const key = `${parentIndex}-${subIdx}`;
    setOpenSubKeySet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const findFirstRouteLink = (item: MenuItem): string | undefined => {
    if (item.menuRouterLink) return item.menuRouterLink;
    if (!item.children || item.children.length === 0) return undefined;
    for (const child of item.children) {
      const found = findFirstRouteLink(child);
      if (found) return found;
    }
    return undefined;
  };

  const getIconForPath = (path?: string) => {
    if (!path || path === "/") return <DashboardOutlined />;
    if (path.startsWith("/menu")) return <AppstoreOutlined />;
    if (path.startsWith("/role")) return <SafetyOutlined />;
    if (path.startsWith("/code")) return <ProfileOutlined />;
    if (path.startsWith("/terms")) return <FileTextOutlined />;
    if (path.startsWith("/notice")) return <NotificationOutlined />;
    if (path.startsWith("/faq")) return <QuestionCircleOutlined />;
    if (path.startsWith("/delegate")) return <ApartmentOutlined />;
    if (path.startsWith("/brand")) return <ShopOutlined />;
    if (path.startsWith("/collection")) return <AppstoreOutlined />;
    if (path.startsWith("/sku")) return <TagsOutlined />;
    if (path.startsWith("/product")) return <ShoppingOutlined />;
    if (path.startsWith("/popup-banner")) return <PictureOutlined />;
    if (path.startsWith("/customer")) return <TeamOutlined />;
    if (path.startsWith("/manage/user")) return <UserOutlined />;
    if (path.startsWith("/manage/delivery-policy")) return <TruckOutlined />;
    if (path.startsWith("/manage/coupon")) return <GiftOutlined />;
    if (path.startsWith("/promotion")) return <GiftOutlined />;
    return null;
  };

  // 메뉴 아이템의 아이콘 결정
  const getMenuIcon = (item: MenuItem, isTopLevel: boolean) => {
    if (!isTopLevel) return null;
    const name = item.menuName || "";
    if (name.includes("대시보드")) return <DashboardOutlined />;
    if (name.includes("공통")) return <ProfileOutlined />;
    if (name.includes("어드민") || name.includes("관리자")) return <SafetyOutlined />;
    if (name.includes("상품")) return <ShoppingOutlined />;
    const path = item.menuRouterLink || findFirstRouteLink(item);
    const icon = getIconForPath(path);
    if (icon) return icon;
    return item.hasChildren ? <AppstoreOutlined /> : <DashboardOutlined />;
  };

  // 3단계 메뉴 렌더링
  const renderLevel3 = (children: MenuItem[], parentIndex: number, subIdx: number) => {
    if (collapsed) return null;
    const subKey = `${parentIndex}-${subIdx}`;
    const isSubOpen = openSubKeySet.has(subKey);

    if (!isSubOpen) return null;

    return (
      <SubSubMenuWrapper>
        {children.map((leaf, leafIdx) => (
          <StyledLink key={leafIdx} to={leaf.menuRouterLink || "#"}>
            <SubSubMenuItem $active={isActivePath(leaf.menuRouterLink)}>
              {leaf.menuName}
            </SubSubMenuItem>
          </StyledLink>
        ))}
      </SubSubMenuWrapper>
    );
  };

  // 2단계 메뉴 렌더링
  const renderLevel2 = (children: MenuItem[], parentIndex: number) => {
    if (collapsed) return null;
    return (
      <SubMenuWrapper>
        {children.map((sub, subIdx) => {
          const subHasChildren = sub.hasChildren && sub.children?.length > 0;
          const subKey = `${parentIndex}-${subIdx}`;
          const subOpen = openSubKeySet.has(subKey);

          if (!subHasChildren) {
            return (
              <StyledLink key={subIdx} to={sub.menuRouterLink || "#"}>
                <SubMenuItem $active={isActivePath(sub.menuRouterLink)}>{sub.menuName}</SubMenuItem>
              </StyledLink>
            );
          }

          return (
            <React.Fragment key={subIdx}>
              <SubMenuItem
                as="div"
                onClick={() => handleToggleSub(parentIndex, subIdx)}
                $active={false}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{sub.menuName}</span>
                <ArrowIcon $isOpen={subOpen}>
                  <RightOutlined />
                </ArrowIcon>
              </SubMenuItem>

              {renderLevel3(sub.children, parentIndex, subIdx)}
            </React.Fragment>
          );
        })}
      </SubMenuWrapper>
    );
  };

  // 1단계 메뉴 렌더링
  const renderLevel1 = () => {
    // 대시보드 메뉴 추가 (항상 최상단에)
    const dashboardItem = (
      <StyledLink to="/" key="dashboard">
        <MenuItemStyled $active={isActivePath("/")} $collapsed={collapsed}>
          <div className="main-content">
            <DashboardOutlined />
            {!collapsed && <span>대시보드</span>}
          </div>
        </MenuItemStyled>
      </StyledLink>
    );

    const menuItems = menuTree.map((item, index) => {
      const hasChildren = item.hasChildren && item.children?.length > 0;
      const isOpen = openIndexSet.has(index);

      // 자식이 없는 최상위 메뉴
      if (!hasChildren) {
        // "/" 경로는 대시보드에서 이미 처리
        if (item.menuRouterLink === "/") return null;

        return (
          <StyledLink to={item.menuRouterLink || "#"} key={index}>
            <MenuItemStyled $active={isActivePath(item.menuRouterLink)} $collapsed={collapsed}>
              <div className="main-content">
                {getMenuIcon(item, true)}
                {!collapsed && <span>{item.menuName}</span>}
              </div>
            </MenuItemStyled>
          </StyledLink>
        );
      }

      // 자식이 있는 최상위 메뉴
      if (collapsed) {
        return (
          <MenuItemStyled key={index} $collapsed={collapsed}>
            <div className="main-content">{getMenuIcon(item, true)}</div>
          </MenuItemStyled>
        );
      }

      return (
        <React.Fragment key={index}>
          <MenuItemStyled onClick={() => handleToggle(index)} $collapsed={collapsed}>
            <div className="main-content">
              {getMenuIcon(item, true)}
              <span>{item.menuName}</span>
            </div>
            <ArrowIcon $isOpen={isOpen}>
              <RightOutlined />
            </ArrowIcon>
          </MenuItemStyled>

          {isOpen && renderLevel2(item.children, index)}
        </React.Fragment>
      );
    });

    return (
      <>
        {dashboardItem}
        {menuItems}
      </>
    );
  };

  return (
    <SidebarWrapper $collapsed={collapsed}>
      <LogoRow $collapsed={collapsed}>
        <Logo $collapsed={collapsed}>KNOLLO OMS</Logo>
        <ToggleButton
          type="text"
          onClick={() => setCollapsed(!collapsed)}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        />
      </LogoRow>
      {renderLevel1()}
    </SidebarWrapper>
  );
};

export default Sidebar;

// --- Styled Components ---

const SidebarWrapper = styled.aside<{ $collapsed?: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  width: ${({ $collapsed }) => ($collapsed ? "72px" : "240px")};
  background-color: ${myTheme.colors.primary};
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  height: 100vh;
  color: white;
  overflow-y: auto;
  transition: width 0.2s ease;
  z-index: 1000;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const MenuItemStyled = styled.div<{ $active?: boolean; $collapsed?: boolean }>`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 14px;
  background-color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.15)" : "transparent")};

  &:hover {
    background-color: ${myTheme.colors.primaryHover};
  }

  .main-content {
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
    width: 100%;
    overflow: hidden;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  svg {
    margin-right: ${({ $collapsed }) => ($collapsed ? "0" : "10px")};
    font-size: 20px;
  }
`;

const SubMenuWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
`;

const SubMenuItem = styled.div<{ $active?: boolean }>`
  padding: 10px 24px 10px 54px;
  font-size: 13px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "white" : "rgba(255, 255, 255, 0.7)")};
  background-color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.1)" : "transparent")};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const SubSubMenuWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.16);
`;

const SubSubMenuItem = styled.div<{ $active?: boolean }>`
  padding: 9px 24px 9px 74px;
  font-size: 12.5px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "white" : "rgba(255, 255, 255, 0.7)")};
  font-weight: ${({ $active }) => ($active ? "700" : "400")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  display: flex;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
  svg {
    margin-right: 0;
  }
`;

const LogoRow = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "space-between")};
  padding: 0 16px 16px;
`;

const Logo = styled.div<{ $collapsed?: boolean }>`
  font-size: 20px;
  font-weight: bold;
  padding: 0 8px;
  white-space: nowrap;
  overflow: hidden;
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  width: ${({ $collapsed }) => ($collapsed ? "0" : "auto")};
  transition: opacity 0.2s ease;
`;

const ToggleButton = styled(Button)`
  color: white;
  font-size: 18px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.12);
  }

  .anticon {
    font-size: 20px;
  }
`;
